
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import loadingGif from '../../assets/loading.gif';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { IconButton } from '@mui/material';
import './CreateOrganizationRoles.css';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from '@mui/material';

const CreateOrganization = () => {
  const [roleData, setRoleData] = useState(null);
  const [roleName, setRoleName] = useState('New Role');
  const [permissions, setPermissions] = useState({
    'Dashboard': { read: false },
    'Create Order': { create: false },
    'Current Order': { read: false, update: false, delete: false },
    'Finished Order': { read: false, update: false, delete: false },
    'Do Not Proceed': { read: false, update: false, delete: false },
    'Recently Deleted': { read: false, update: false },
    'Tools': { read: false },
    'Organization Settings': { create: false, read: false, update: false, delete: false },
    'Organization Roles': { create: false, read: false, update: false, delete: false },
    'Organization Members': { create: false, read: false, update: false, delete: false },
    'Product Management': { create: false, read: false, update: false, delete: false },
    'Messages': { read: false },
  });

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/users/admin/role-info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization info');
        }

        const data = await response.json();
        setRoleData(data);
      } catch (error) {
        console.error('Error fetching admin status:', error);
        navigate('/unauthorized');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminStatus();
  }, [navigate]);

  const calculateUsagePercentage = () => {
    if (roleData) {
      return Math.floor((roleData.role_count / roleData.max_role) * 100);
    }
    return 0;
  };

  const isCreateButtonEnabled = roleData && roleData.role_count < roleData.max_role;

  const handleCreateRole = async () => {
    if (!roleName || !permissions) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill out all fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } else {
      try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');
  
        const response = await fetch(`${API_BASE_URL}/org-roles/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
          body: JSON.stringify({
            roleName: roleName,
            permissions: permissions,
          }),
        });
  
        if (response.status === 201) {
          const data = await response.json();
  
          Swal.fire({
            title: 'Success!',
            text: 'Role created successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/organization/roles');
          });
        } else {
          throw new Error('Failed to create role');
        }
  
      } catch (error) {
        console.error('Error creating role:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to create role.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  const handleToggle = (feature, action) => {
    setPermissions((prev) => {
      const newPermissions = {
        ...prev,
        [feature]: {
          ...prev[feature],
          [action]: !prev[feature][action],
        },
      };
  
      // Check conditions for permissions
      if (action === 'read') {
        // If 'read' is turned off, turn off 'update' and 'delete'
        if (!newPermissions[feature].read) {
            if (newPermissions[feature].update !== undefined) {
                newPermissions[feature].update = false;
            }
            if (newPermissions[feature].delete !== undefined) {
                newPermissions[feature].delete = false;
            }
        }
      }
  
      if (action === 'update') {
        // If 'update' is turned on, ensure 'read' is also turned on
        if (newPermissions[feature].update) {
            if (newPermissions[feature].read !== undefined) {
                newPermissions[feature].read = true;
            }
        }
        // If 'update' is turned off, turn off 'delete'
        else {
            if (newPermissions[feature].delete !== undefined) {
                newPermissions[feature].delete = false;
            }
        }
      }
  
      if (action === 'delete') {
        // If 'delete' is turned on, ensure both 'read' and 'update' are turned on
        if (newPermissions[feature].delete) {
            if (newPermissions[feature].read !== undefined) {
                newPermissions[feature].read = true;
            }
            if (newPermissions[feature].update !== undefined) {
                newPermissions[feature].update = true;
            }
        }
      }
  
      return newPermissions;
    });
  };
  
  if (loading) {
    return (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
    }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
    </div>
    );
  }

  const usagePercentage = calculateUsagePercentage();


  return (
    <Box sx={{ width: '100%', margin: 'auto', mt: 0 }}>
      <Card sx={{ width: '100%', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <IconButton onClick={() => navigate('/organization/roles')} sx={{ mr: 2 }}>
            <KeyboardReturnRoundedIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Create Organization Role
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 3 }}>
          {roleData && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Role Usage: {roleData.role_count} / {roleData.max_role} roles
              </Typography>
              <Box sx={{ mb: 3 }}>
                <LinearProgress variant="determinate" value={usagePercentage} sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            </>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={12}>
              <TextField
                label="Role Name"
                value={roleName}
                onChange={e => setRoleName(e.target.value)}
                fullWidth
                multiline
                rows={1}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
          <Divider />
          <TableContainer component={Paper} sx={{ mb: 3, mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><b>Features</b></TableCell>
                  <TableCell align="center"><b>Create</b></TableCell>
                  <TableCell align="center"><b>Read</b></TableCell>
                  <TableCell align="center"><b>Update</b></TableCell>
                  <TableCell align="center"><b>Delete</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(permissions).map((feature) => (
                  <TableRow key={feature}>
                    <TableCell>{feature}</TableCell>
                    <TableCell>
                      {permissions[feature].create !== undefined ? (
                        <Switch
                          checked={permissions[feature].create}
                          onChange={() => handleToggle(feature, 'create')}
                          color="primary"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {permissions[feature].read !== undefined ? (
                        <Switch
                          checked={permissions[feature].read}
                          onChange={() => handleToggle(feature, 'read')}
                          color="primary"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {permissions[feature].update !== undefined ? (
                        <Switch
                          checked={permissions[feature].update}
                          onChange={() => handleToggle(feature, 'update')}
                          color="primary"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {permissions[feature].delete !== undefined ? (
                        <Switch
                          checked={permissions[feature].delete}
                          onChange={() => handleToggle(feature, 'delete')}
                          color="primary"
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateRole}
            disabled={!isCreateButtonEnabled}
            sx={{ mt: 3 }}
          >
            Create Role
          </Button>
          {!isCreateButtonEnabled && (
            <Typography color="error" sx={{ mt: 2 }}>
              * You have reached the maximum limit for roles allowed under your plan. Please upgrade your plan or delete an existing role before creating a new one.
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default CreateOrganization;
